/**
 * Country → State/Region → Location: the shared geographic scope, as pure rules.
 *
 * Everything here is derived from the locations the API says this login may
 * work in. Nothing is hardcoded about any one country: a country's divisions
 * come from the locations' own resolved `stateCode`s (named through the shared
 * region list), and a country whose locations name none simply has no
 * State/Region step.
 *
 * Kept free of React so the cascade — what each step offers, what a change
 * resets, what a remembered choice is still worth — is testable on its own.
 */

import { findCountry, findState, regionTermOf, type RegionTerm } from '@cenox/shared';

/** The `state` value for "locations in this country that name no state". */
export const UNSET_REGION = '__unset__';

export interface ScopedLocation {
  id: string;
  name: string;
  countryCode?: string;
  stateCode?: string | null;
}

/** A selection. Null at any step is "All" at that step. */
export interface GeoSelection {
  country: string | null;
  region: string | null;
  branchId: string | null;
}

export const EVERYWHERE: GeoSelection = { country: null, region: null, branchId: null };

export interface Option {
  value: string;
  label: string;
}

/** A location's country, falling back to the business's as the API does. */
export function countryOfLocation(l: ScopedLocation, businessCountry: string | null | undefined): string {
  return (l.countryCode ?? businessCountry ?? '').toUpperCase();
}

function regionOfLocation(l: ScopedLocation): string {
  return l.stateCode ? l.stateCode.toUpperCase() : UNSET_REGION;
}

export function countryName(code: string): string {
  return findCountry(code)?.name ?? code;
}

export function regionName(country: string, region: string): string {
  if (region === UNSET_REGION) return `No ${regionTermOf(country).singular.toLowerCase()} set`;
  return findState(country, region)?.name ?? region;
}

/** The countries this login has a location in, by name. */
export function countryOptions(locations: ScopedLocation[], businessCountry: string | null | undefined): Option[] {
  const codes = [...new Set(locations.map((l) => countryOfLocation(l, businessCountry)).filter(Boolean))];
  return codes
    .map((code) => ({ value: code, label: countryName(code) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The divisions of one country this login has a location in.
 *
 * Empty when the country's locations name no division at all — that country
 * has no State/Region step. "No state set" appears only beside real ones, for
 * the locations that were never given one.
 */
export function regionOptions(
  locations: ScopedLocation[],
  businessCountry: string | null | undefined,
  country: string | null,
): Option[] {
  if (!country) return [];
  const inCountry = locations.filter((l) => countryOfLocation(l, businessCountry) === country);
  const codes = [...new Set(inCountry.map(regionOfLocation))];
  if (codes.length === 1 && codes[0] === UNSET_REGION) return [];
  return codes
    .map((code) => ({ value: code, label: regionName(country, code) }))
    .sort((a, b) => (a.value === UNSET_REGION ? 1 : b.value === UNSET_REGION ? -1 : a.label.localeCompare(b.label)));
}

/** The locations inside a country and region (either may be "All"). */
export function locationsIn(
  locations: ScopedLocation[],
  businessCountry: string | null | undefined,
  country: string | null,
  region: string | null,
): ScopedLocation[] {
  return locations.filter(
    (l) =>
      (!country || countryOfLocation(l, businessCountry) === country) &&
      (!region || regionOfLocation(l) === region),
  );
}

/**
 * Hold a selection to what this login may currently see.
 *
 * Used for a remembered selection, after a business switch, and whenever the
 * permitted list changes. Each step is kept only if it still exists under the
 * step above it; a location wins over stale parents (its own country and
 * region are re-derived from it), and anything that no longer resolves falls
 * back to "All" at that step — never to a guess at a neighbour.
 *
 * `canAggregate` false (a login confined to one location) always resolves to a
 * single location: there is no "All" for it to land on.
 */
export function normaliseSelection(
  wanted: GeoSelection,
  locations: ScopedLocation[],
  businessCountry: string | null | undefined,
  canAggregate: boolean,
  fallbackBranchId: string | null,
): GeoSelection {
  const byId = new Map(locations.map((l) => [l.id, l]));
  const pin = (id: string | null): GeoSelection | null => {
    const l = id ? byId.get(id) : undefined;
    if (!l) return null;
    const country = countryOfLocation(l, businessCountry) || null;
    const hasRegions = country !== null && regionOptions(locations, businessCountry, country).length > 0;
    return { country, region: hasRegions ? regionOfLocation(l) : null, branchId: l.id };
  };

  if (!canAggregate) {
    return pin(wanted.branchId) ?? pin(fallbackBranchId) ?? pin(locations[0]?.id ?? null) ?? EVERYWHERE;
  }

  const pinned = pin(wanted.branchId);
  if (pinned) return pinned;

  const countries = countryOptions(locations, businessCountry).map((o) => o.value);
  const country = wanted.country && countries.includes(wanted.country.toUpperCase()) ? wanted.country.toUpperCase() : null;
  if (!country) return EVERYWHERE;

  const regions = regionOptions(locations, businessCountry, country).map((o) => o.value);
  const region = wanted.region && regions.includes(wanted.region) ? wanted.region : null;
  return { country, region, branchId: null };
}

/** Change the country: an incompatible region and location are dropped. */
export function withCountry(sel: GeoSelection, country: string | null): GeoSelection {
  if (country === sel.country) return sel;
  return { country, region: null, branchId: null };
}

/** Change the region: an incompatible location is dropped. */
export function withRegion(
  sel: GeoSelection,
  region: string | null,
  locations: ScopedLocation[],
  businessCountry: string | null | undefined,
): GeoSelection {
  if (region === sel.region) return sel;
  const keep =
    sel.branchId && locationsIn(locations, businessCountry, sel.country, region).some((l) => l.id === sel.branchId);
  return { ...sel, region, branchId: keep ? sel.branchId : null };
}

/** The query parameters a scoped read sends. Empty for "everything". */
export function scopeParams(sel: GeoSelection): { branchId?: string; scopeCountry?: string; scopeState?: string } {
  if (sel.branchId) return { branchId: sel.branchId };
  if (!sel.country) return {};
  return sel.region ? { scopeCountry: sel.country, scopeState: sel.region } : { scopeCountry: sel.country };
}

/** "Nigeria › Enugu › Fire Service", or "All countries". */
export function describeSelection(
  sel: GeoSelection,
  locationName: string | null,
): { parts: string[]; everywhere: boolean } {
  if (!sel.country && !sel.branchId) return { parts: ['All countries'], everywhere: true };
  const parts: string[] = [];
  if (sel.country) parts.push(countryName(sel.country));
  if (sel.country && sel.region) parts.push(regionName(sel.country, sel.region));
  parts.push(sel.branchId ? (locationName ?? 'One location') : 'All locations');
  return { parts, everywhere: false };
}

export type { RegionTerm };
export { regionTermOf };
